import { ReadCvLogo, type Icon } from "@phosphor-icons/react";
import { AssessmentType } from "@constants/assessmentTypes";

export interface DocumentTypeConfig {
  /** URL-safe key used for the checklist API endpoint */
  key: string;
  /** Display title shown in cards, headers, and confirm step */
  title: string;
  /** Short description shown under the title on the select-step card */
  cardDescription: string;
  /** Phosphor icon component */
  icon: Icon;
  /** Description shown in the confirm step card body */
  getDescription: (clientName: string) => string;
  /** When true, always show the Review Assessments step even if status is "Ready" */
  requiresAssessmentsReview: boolean;
  /** Assessment types that are required for this document — others are treated as Available */
  requiredAssessmentTypes: AssessmentType[];
  /** Which goals step to render. "none" skips the goals step entirely. */
  goalsView: "withoutProgress" | "withProgress" | "hoursAddendum" | "none";
  /** When true, block generation unless at least one goal will be included/sent to MIRA. */
  requiresAtLeastOneGoal: boolean;
  /** Identifier embedded in the MIRA targetProcess name (e.g. "initialTreatmentPlan"). */
  miraDocumentType:
    | "initialTreatmentPlan"
    | "reassessment"
    | "90DayProgress"
    | "incidentReport"
    | "hoursAddendumRequest"
    | "dischargeReport";
  registerType:
    | "INITIAL_TREATMENT_PLAN"
    | "RE_ASSESSMENT_TREATMENT_PLAN"
    | "INCIDENT_REPORT"
    | "HOURS_ADDENDUM_REQUEST"
    | "DISCHARGE_REPORT"
    | "90_DAY_PROGRESS";
}

export const DOCUMENT_TYPES: Record<string, DocumentTypeConfig> = {
  initial_treatment_plan: {
    key: "initial_treatment_plan",
    title: "Initial Treatment Plan",
    cardDescription: "Baseline assessment results and goals for the first 6 months",
    icon: ReadCvLogo,
    getDescription: (clientName) =>
      `We'll compile all required data from ${clientName}'s account into a formatted Initial Treatment Plan.`,
    requiresAssessmentsReview: true,
    requiredAssessmentTypes: ["VINELAND", "CFQL2", "QABF"],
    goalsView: "withoutProgress",
    requiresAtLeastOneGoal: true,
    miraDocumentType: "initialTreatmentPlan",
    registerType: "INITIAL_TREATMENT_PLAN",
  },
  reassessment_treatment_plan: {
    key: "reassessment_treatment_plan",
    title: "Re-assessment Treatment Plan",
    cardDescription:
      "Re-assessment results, progress review, and updated goals for the next 6 months",
    icon: ReadCvLogo,
    getDescription: (clientName) =>
      `We'll compile all required data from ${clientName}'s account into a formatted Reassessment Treatment Plan.`,
    requiresAssessmentsReview: true,
    requiredAssessmentTypes: ["VINELAND", "CFQL2", "QABF"],
    goalsView: "withProgress",
    requiresAtLeastOneGoal: true,
    miraDocumentType: "reassessment",
    registerType: "RE_ASSESSMENT_TREATMENT_PLAN",
  },
  "90_day_progress": {
    key: "90_day_progress",
    title: "90 Day Progress Summary",
    cardDescription: "Early progress summary and goals for the first 90 days",
    icon: ReadCvLogo,
    getDescription: (clientName) =>
      `We'll compile all required data from ${clientName}'s account into a formatted 90 Day Progress Summary.`,
    requiresAssessmentsReview: true,
    requiredAssessmentTypes: ["VINELAND", "CFQL2", "QABF"],
    goalsView: "withProgress",
    requiresAtLeastOneGoal: true,
    miraDocumentType: "90DayProgress",
    registerType: "90_DAY_PROGRESS",
  },
  incident_report: {
    key: "incident_report",
    title: "Incident Report",
    cardDescription: "Documentation of a safety-related event during a session",
    icon: ReadCvLogo,
    getDescription: (clientName) =>
      `We'll compile the incident details for ${clientName} into a formatted Incident Report.`,
    requiresAssessmentsReview: false,
    requiredAssessmentTypes: [],
    goalsView: "none",
    requiresAtLeastOneGoal: false,
    miraDocumentType: "incidentReport",
    registerType: "INCIDENT_REPORT",
  },
  hours_addendum_request: {
    key: "hours_addendum_request",
    title: "Hours Addendum Request",
    cardDescription: "Request and clinical rationale for increasing authorized hours",
    icon: ReadCvLogo,
    getDescription: (clientName) =>
      `We'll compile ${clientName}'s data into a formatted Hours Addendum Request.`,
    requiresAssessmentsReview: false,
    requiredAssessmentTypes: [],
    // HAR treats every included goal as "new". Unlike ITP, missing-baseline goals are NOT
    // excluded — an hours request needs to surface every relevant goal regardless of whether
    // baseline data has been collected yet.
    goalsView: "hoursAddendum",
    requiresAtLeastOneGoal: false,
    miraDocumentType: "hoursAddendumRequest",
    registerType: "HOURS_ADDENDUM_REQUEST",
  },
  discharge_report: {
    key: "discharge_report",
    title: "Discharge Report",
    cardDescription: "Reason for discharge, transition plan, and re-entry guidance",
    icon: ReadCvLogo,
    getDescription: (clientName) =>
      `We'll compile ${clientName}'s data into a formatted Discharge Report.`,
    requiresAssessmentsReview: false,
    requiredAssessmentTypes: [],
    goalsView: "none",
    requiresAtLeastOneGoal: false,
    miraDocumentType: "dischargeReport",
    registerType: "DISCHARGE_REPORT",
  },
};
